'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import {
  Library, ChevronLeft, ChevronRight, Settings, CreditCard, HelpCircle, LogOut,
  Zap, Home, Globe, User, Image as ImageIcon, Film, Layers, Hash, Smartphone,
  BarChart2, Type, Target, BarChart, Music, Clapperboard, Scissors, Mic,
  Captions, Wand2, ChevronDown, ChevronUp, Wrench,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useCredits } from '@/lib/hooks/useCredits';
import { useRouter } from 'next/navigation';

interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: React.ReactNode;
  badge?: string | number;
  badgeVariant?: 'primary' | 'accent' | 'muted' | 'hot';
}

interface SidebarProps {
  activePath: string;
}

// Caption Engine core nav items
const captionItems: NavItem[] = [
  { id: 'nav-caption-engine', label: 'Caption Engine', href: '/caption-engine', icon: <Captions size={18} /> },
  { id: 'nav-subtitle-presets', label: 'Style Presets', href: '/subtitle-presets', icon: <Type size={18} /> },
  { id: 'nav-font-manager', label: 'Font Manager', href: '/font-manager', icon: <Wand2 size={18} /> },
];

// All other tools — collapsed into a single "Tools" section
const toolItems: NavItem[] = [
  { id: 'nav-home', label: 'Home', href: '/home', icon: <Home size={18} /> },
  { id: 'nav-image-gen', label: 'Image Generator', href: '/image-generator', icon: <ImageIcon size={18} /> },
  { id: 'nav-video-gen', label: 'Video Generator', href: '/video-generator', icon: <Film size={18} /> },
  { id: 'nav-number-gen', label: 'Number Generator', href: '/number-generator', icon: <Hash size={18} /> },
  { id: 'nav-progress-bar', label: 'Progress Bars', href: '/progress-bar', icon: <BarChart2 size={18} /> },
  { id: 'nav-chart-gen', label: 'Chart Generator', href: '/chart-generator', icon: <BarChart size={18} /> },
  { id: 'nav-fake-ui', label: 'Fake UI Generator', href: '/fake-ui', icon: <Smartphone size={18} /> },
  { id: 'nav-callout-gen', label: 'Callout Generator', href: '/callout-generator', icon: <Target size={18} /> },
  { id: 'nav-asset-vault', label: 'Asset Vault', href: '/asset-vault', icon: <Layers size={18} /> },
  { id: 'nav-sound-fx', label: 'Sound FX Library', href: '/sound-fx', icon: <Music size={18} /> },
  { id: 'nav-broll', label: 'B-Roll Finder', href: '/broll-finder', icon: <Clapperboard size={18} /> },
  { id: 'nav-viral-hook', label: 'Viral Hook Generator', href: '/viral-hook-generator', icon: <Zap size={18} /> },
  { id: 'nav-bg-remover', label: 'Background Remover', href: '/background-remover', icon: <Scissors size={18} /> },
  { id: 'nav-transcriptor', label: 'Transcriptor', href: '/transcriptor', icon: <Mic size={18} /> },
  { id: 'nav-library', label: 'My Library', href: '/my-library', icon: <Library size={18} /> },
  { id: 'nav-public-library', label: 'Public Library', href: '/public-library', icon: <Globe size={18} /> },
];

const bottomNavItems: NavItem[] = [
  { id: 'nav-billing', label: 'Billing & Plan', href: '/billing', icon: <CreditCard size={18} /> },
  { id: 'nav-settings', label: 'Settings', href: '/settings', icon: <Settings size={18} /> },
  { id: 'nav-help', label: 'Help & Docs', href: '#', icon: <HelpCircle size={18} /> },
];

interface SidebarContentProps {
  collapsed: boolean;
  onCollapse: () => void;
  activePath: string;
}

function SidebarContent({ collapsed, onCollapse, activePath }: SidebarContentProps) {
  const { user, signOut } = useAuth();
  const { balance, loading: creditsLoading } = useCredits(user?.id ?? null);
  const router = useRouter();
  const [toolsOpen, setToolsOpen] = useState(false);

  const isActive = (href: string) => {
    if (href === '/') return activePath === '/';
    return activePath.startsWith(href);
  };

  // Auto-open tools section if current path is a tool
  const isToolActive = toolItems.some(item => isActive(item.href));

  const handleSignOut = async () => {
    try {
      await signOut();
      router.push('/sign-up-login');
    } catch {}
  };

  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User';
  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  const email = user?.email || '';
  const creditsDisplay = creditsLoading ? '…' : (balance ?? 0).toLocaleString();

  const showTools = toolsOpen || isToolActive;

  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={`flex items-center h-14 border-b border-border px-4 ${collapsed ? 'justify-center' : 'gap-3'}`}>
        <div className="flex items-center gap-2 flex-shrink-0">
          <AppLogo size={collapsed ? 32 : 36} />
          {!collapsed && (
            <div>
              <span className="font-black text-base tracking-tight text-foreground">
                MoGur
              </span>
              <p className="text-[9px] text-muted-foreground font-medium tracking-widest uppercase leading-none">Caption Engine</p>
            </div>
          )}
        </div>
        {!collapsed && (
          <span className="ml-auto text-[10px] font-medium px-1.5 py-0.5 rounded bg-secondary text-muted-foreground border border-border tracking-wide uppercase">
            Beta
          </span>
        )}
      </div>

      {/* Credits Badge */}
      {!collapsed && (
        <div className="mx-3 mt-3 mb-1 px-3 py-2.5 rounded-lg bg-secondary/50 border border-border">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-1.5">
              <Zap size={12} className="text-foreground flex-shrink-0" />
              <p className="text-[10px] font-semibold text-muted-foreground tracking-wide uppercase">Credits</p>
            </div>
            <Link href="/billing" className="text-[10px] text-muted-foreground hover:text-foreground transition-colors">
              Add
            </Link>
          </div>
          <p className="text-lg font-bold text-foreground tabular-nums leading-none">{creditsDisplay}</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">available</p>
        </div>
      )}

      {/* Main Nav */}
      <nav className="flex-1 px-2 py-2 overflow-y-auto scrollbar-thin space-y-0.5">
        {/* Caption Engine items */}
        {!collapsed && (
          <p className="text-[10px] font-semibold text-muted-foreground tracking-widest uppercase px-2 py-1 mb-0.5 mt-1">
            Caption Engine
          </p>
        )}
        {captionItems.map((item) => (
          <Link
            key={item.id}
            href={item.href}
            className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150
              ${isActive(item.href)
                ? 'bg-foreground/8 text-foreground border border-border'
                : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
              }
              ${collapsed ? 'justify-center px-2' : ''}
            `}
          >
            {isActive(item.href) && !collapsed && (
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-foreground/40 rounded-full" />
            )}
            <span className="flex-shrink-0">{item.icon}</span>
            {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
            {collapsed && (
              <div className="absolute left-full ml-2 px-2 py-1 bg-secondary border border-border rounded text-xs text-foreground whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-50">
                {item.label}
              </div>
            )}
          </Link>
        ))}

        {/* Tools section — collapsible */}
        <div className="pt-2">
          <button
            onClick={() => setToolsOpen(o => !o)}
            className={`group relative w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-all duration-150
              ${collapsed ? 'justify-center px-2' : ''}
            `}
          >
            <Wrench size={16} className="flex-shrink-0" />
            {!collapsed && (
              <>
                <span className="flex-1 text-left text-[10px] font-semibold tracking-widest uppercase">Tools</span>
                {showTools ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              </>
            )}
            {collapsed && (
              <div className="absolute left-full ml-2 px-2 py-1 bg-secondary border border-border rounded text-xs text-foreground whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-50">
                Tools
              </div>
            )}
          </button>

          {showTools && (
            <div className="space-y-0.5 mt-0.5">
              {toolItems.map((item) => (
                <Link
                  key={item.id}
                  href={item.href}
                  className={`group relative flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150
                    ${isActive(item.href)
                      ? 'bg-foreground/8 text-foreground border border-border'
                      : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                    }
                    ${collapsed ? 'justify-center px-2' : ''}
                  `}
                >
                  <span className="flex-shrink-0">{item.icon}</span>
                  {!collapsed && <span className="flex-1 truncate text-xs">{item.label}</span>}
                  {collapsed && (
                    <div className="absolute left-full ml-2 px-2 py-1 bg-secondary border border-border rounded text-xs text-foreground whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-50">
                      {item.label}
                    </div>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Bottom Nav */}
      <div className="px-2 pb-2 border-t border-border pt-2 space-y-0.5">
        {bottomNavItems.map((item) => (
          <Link
            key={item.id}
            href={item.href}
            className={`group relative flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 text-muted-foreground hover:text-foreground hover:bg-secondary ${collapsed ? 'justify-center px-2' : ''}`}
          >
            <span className="flex-shrink-0">{item.icon}</span>
            {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
            {collapsed && (
              <div className="absolute left-full ml-2 px-2 py-1 bg-secondary border border-border rounded text-xs text-foreground whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-50">
                {item.label}
              </div>
            )}
          </Link>
        ))}

        {/* User Row */}
        <Link
          href="/profile"
          className={`group flex items-center gap-3 px-3 py-2 mt-1 rounded-lg border border-border bg-secondary/30 hover:bg-secondary transition-all duration-150 ${collapsed ? 'justify-center px-2' : ''}`}
        >
          <div className="w-7 h-7 rounded-full bg-secondary border border-border flex items-center justify-center flex-shrink-0">
            <span className="text-[10px] font-bold text-foreground">{initials || 'U'}</span>
          </div>
          {!collapsed && (
            <>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-foreground truncate">{displayName}</p>
                <p className="text-[10px] text-muted-foreground truncate">{email}</p>
              </div>
              <button
                onClick={(e) => { e.preventDefault(); handleSignOut(); }}
                className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                title="Sign out"
              >
                <LogOut size={13} />
              </button>
            </>
          )}
          {collapsed && (
            <div className="absolute left-full ml-2 px-2 py-1 bg-secondary border border-border rounded text-xs text-foreground whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-50">
              {displayName}
            </div>
          )}
        </Link>
      </div>

      {/* Collapse toggle */}
      <button
        onClick={onCollapse}
        className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-card border border-border flex items-center justify-center text-muted-foreground hover:text-foreground transition-all duration-150 z-10"
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </div>
  );
}

export default function Sidebar({ activePath }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside
      className={`relative flex-shrink-0 h-screen border-r border-border bg-card transition-all duration-200 ${collapsed ? 'w-[60px]' : 'w-[220px]'}`}
    >
      <SidebarContent collapsed={collapsed} onCollapse={() => setCollapsed(c => !c)} activePath={activePath} />
    </aside>
  );
}